import React, { useMemo, useState } from 'react';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import {
  KeywordColumnDefinition,
  ProductColumnDefinition,
  getWeekColumnDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { useProductEntity, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { TrafficGridTopics } from './TrafficGridTopics';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useMetricByWeekId } from 'src/serverProxy/useMetricByWeekId';
import { useRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';

enum TotalTrafficDropdownOption {
  Keyword = 'searchTerm',
  Product = 'stacklineSku',
  Week = 'weekId'
}

const totalTrafficDropdownOptionToColumnDefinition = {
  [TotalTrafficDropdownOption.Keyword]: KeywordColumnDefinition,
  [TotalTrafficDropdownOption.Product]: ProductColumnDefinition,
  [TotalTrafficDropdownOption.Week]: getWeekColumnDefinition({
    getTableColumnProps(defaults) {
      return {
        ...defaults,
        style: {
          ...defaults.style,
          width: '220px'
        }
      };
    }
  })
};

const totalTrafficDropdownOptionToDisplayName = {
  [TotalTrafficDropdownOption.Keyword]: 'Keyword',
  [TotalTrafficDropdownOption.Product]: 'Product',
  [TotalTrafficDropdownOption.Week]: 'Week'
};

const fields = [
  {
    name: 'organicClicks',
    displayName: 'Organic\nTraffic'
  },
  {
    name: 'adClicks',
    displayName: 'Paid\nTraffic'
  },
  {
    name: 'totalClicks',
    displayName: 'Total\nTraffic'
  },
  {
    name: 'adSpend',
    displayName: 'Paid\nAd Spend'
  },
  {
    name: 'adRetailSales',
    displayName: 'Paid\nAd Sales'
  }
];

const dpvIndexName = 'advertisingDisplay';

/**
 * Grid for total traffic by keyword and product
 */
export default function TotalTrafficGrid() {
  const [dropdownOption, setDropdownOption] = useState<TotalTrafficDropdownOption>(TotalTrafficDropdownOption.Keyword);
  const { isProductEntity } = useProductEntity();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters(dpvIndexName);

  const weekEndingFields = fields.map(removeComparisonFormatter);
  const mainEntityType = useMainEntityType();

  const { primaryTotal: currentDspClicks, secondaryTotal: comparisonDspClicks, isLoading: dspLoading } = useMetricByWeekId({
    indexName: dpvIndexName,
    fieldName: 'dpv',
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: dropdownOption === TotalTrafficDropdownOption.Keyword
    },
    requestBuilder: (builder: AdvancedSearchRequestBuilder) => {
      return builder.apply(removeRetailPriceRangeFilters);
    }
  });

  const dspTrafficRow = useMemo(() => ({
    searchTerm: "[DSP Traffic]",
    name: "[DSP Traffic]",
    organicClicks: { value: 0, difference: 0 },
    adClicks: { value: 0, difference: 0 },
    totalClicks: { value: currentDspClicks, difference: currentDspClicks - comparisonDspClicks },
    adSpend: { value: 0, difference: 0 },
    adRetailSales: { value: 0, difference: 0 }
  }), [currentDspClicks, comparisonDspClicks, dspLoading]);

  if (isProductEntity) {
    return (
      <MetricListContainer
        showGrid={false}
        entityTableHeaderProps={{
          prefixTitle: 'Total Traffic by',
          title: '',
          groupByFields: [TotalTrafficDropdownOption.Keyword, TotalTrafficDropdownOption.Week].map((option) => ({
            name: option,
            displayName: totalTrafficDropdownOptionToDisplayName[option]
          })),
          handleGroupByChange: (event) => setDropdownOption(event.target.value as TotalTrafficDropdownOption)
        }}
        indexName="traffic"
        sortFieldName="totalClicks"
        fields={weekEndingFields}
        headColumnDefinition={totalTrafficDropdownOptionToColumnDefinition[dropdownOption]}
        useTableDataProps={{
          groupByFieldName: dropdownOption,
          filterResponse: () => true,
          itemsPerPage: 100,
          buildRequest: (builder: AdvancedSearchRequestBuilder) => {
            return builder.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Traffic]']);
          }
        }}
        postProcessData={(data) => {
          const newData = dropdownOption === TotalTrafficDropdownOption.Keyword ? [dspTrafficRow, ...data] : data;
          if (dropdownOption === TotalTrafficDropdownOption.Week) {
            return newData.sort((a, b) => b.weekId.localeCompare(a.weekId));
          } else {
            return newData.sort((a, b) => b.totalClicks.value - a.totalClicks.value);
          }
        }}
      />
    );
  }

  if (mainEntityType === 'topicgroup' || mainEntityType === 'topic') {
    return <TrafficGridTopics trafficType="totalTraffic" />;
  }

  return (
    <MetricListContainer
      skeletonRows={20}
      showGrid={dropdownOption === TotalTrafficDropdownOption.Product}
      renderGrid={(args) => <ProductGrid {...args} />}
      headColumnDefinition={totalTrafficDropdownOptionToColumnDefinition[dropdownOption]}
      entityTableHeaderProps={{
        prefixTitle: 'Total Traffic by',
        title: '',
        groupByFields: [TotalTrafficDropdownOption.Keyword, TotalTrafficDropdownOption.Product].map((option) => ({
          name: option,
          displayName: totalTrafficDropdownOptionToDisplayName[option]
        })),
        handleGroupByChange: (event) => setDropdownOption(event.target.value as TotalTrafficDropdownOption)
      }}
      indexName="traffic"
      fields={fields}
      useTableDataProps={{
        groupByFieldName: dropdownOption,
        filterResponse: () => true,
        buildRequest: (builder: AdvancedSearchRequestBuilder) => {
          return builder.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Traffic]']);
        }
      }}
      postProcessData={(data) => {
        if (dropdownOption === TotalTrafficDropdownOption.Keyword) {
          const newData = [dspTrafficRow, ...data];
          return newData.sort((a, b) => b.totalClicks.value - a.totalClicks.value);
        }

        return data;
      }}
      sortFieldName="totalClicks"
    />
  );
}
